import React, { useState, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './LabelEditor.css';

const LH = 0.424;
const PT_MM = 0.353;

function lhMm(pt) { return pt * LH; }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

const SAFE = 0.5; // mm — margen de seguridad mínimo desde el borde de la etiqueta

// Calcula el layout inicial a partir de los datos del item (cuando no hay layout guardado).
function computeLayout(item, W, H) {
  const qrScale = clamp(typeof item.qrScale === 'number' ? item.qrScale : 1, 0.45, 1);
  const qrSize  = (H * 0.95) * qrScale;
  const SIDE = 1, GAP = 1.5, TB = 1, LGAP = 0.5;
  const prFontPt = typeof item.priceSize === 'number' ? item.priceSize : 9;
  const nFontPt  = typeof item.nameSize  === 'number' ? item.nameSize  : 4;
  const vFontPt  = typeof item.varSize   === 'number' ? item.varSize   : 3.5;
  const price = (item.price || '').trim();
  const name  = (item.productName || '').trim();
  const varia = (item.variationLabel || '').trim();

  let totalH = 0;
  if (price) { totalH += lhMm(prFontPt); }
  if (name)  { if (totalH) totalH += LGAP; totalH += lhMm(nFontPt); }
  if (varia) { if (totalH) totalH += LGAP; totalH += lhMm(vFontPt); }

  const usableH = H - 2 * TB;
  const textTop = TB + Math.max(0, (usableH - totalH) / 2);
  const textX   = SIDE + qrSize + GAP;
  let curY = textTop;

  const layout = { qr: { x: SIDE, y: (H - qrSize) / 2, size: qrSize } };
  if (price) { layout.price     = { x: textX, y: curY, fontPt: prFontPt }; curY += lhMm(prFontPt) + LGAP; }
  if (name)  { layout.name      = { x: textX, y: curY, fontPt: nFontPt  }; curY += lhMm(nFontPt)  + LGAP; }
  if (varia) { layout.variation = { x: textX, y: curY, fontPt: vFontPt  }; }
  return layout;
}

/*
  LabelEditor — editor interactivo de etiqueta con drag para mover y
  corner-drag para cambiar tamaño.

  Props:
    item           { qrValue, productName, variationLabel, price, sku, loteNumero,
                     qrScale, priceSize, nameSize, varSize }
    config         { width, height } mm
    initialLayout  layout guardado o null (auto-calcula)
    onLayoutChange callback(layout) llamado al soltar el arrastre
*/
export default function LabelEditor({ item, config, initialLayout, onLayoutChange, maxWidthPx = 260 }) {
  const W = Math.max(5, parseFloat(config?.width)  || 40);
  const H = Math.max(5, parseFloat(config?.height) || 12);

  // Escala limitada al ancho disponible del contenedor modal (~260px en qrm-card)
  const scale = Math.min(maxWidthPx / W, 200 / H);

  const [layout,   setLayout]   = useState(() => initialLayout || computeLayout(item, W, H));
  const [selected, setSelected] = useState(null); // 'qr' | 'price' | 'name' | 'variation'

  const dragRef     = useRef(null);
  const layoutRef   = useRef(layout);
  const containerRef = useRef(null);

  const px   = (mm) => mm * scale;
  const ptPx = (pt) => pt * PT_MM * scale;

  const toMm = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale };
  }, [scale]);

  const startDrag = useCallback((e, elemKey, action, extra = {}) => {
    e.stopPropagation();
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    dragRef.current = {
      elemKey, action, ...extra,
      startPos: toMm(e.clientX, e.clientY),
      startLayout: JSON.parse(JSON.stringify(layoutRef.current)),
    };
    setSelected(elemKey);
  }, [toMm]);

  const handlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const cur = toMm(e.clientX, e.clientY);
    const dx  = cur.x - drag.startPos.x;
    const dy  = cur.y - drag.startPos.y;
    const el  = drag.startLayout[drag.elemKey];
    if (!el) return;

    setLayout(prev => {
      const next = { ...prev };

      if (drag.elemKey === 'qr') {
        if (drag.action === 'move') {
          next.qr = {
            ...el,
            x: clamp(el.x + dx, SAFE, W - el.size - SAFE),
            y: clamp(el.y + dy, SAFE, H - el.size - SAFE),
          };
        } else {
          // resize: el signo depende de la esquina arrastrada
          const xs = drag.corner.includes('e') ? 1 : -1;
          const ys = drag.corner.includes('s') ? 1 : -1;
          const delta = (xs * dx + ys * dy) * 0.65;
          const maxSize = Math.min(W, H) - 2 * SAFE;
          const newSize = clamp(el.size + delta, 3, maxSize);
          const newX = drag.corner.includes('w')
            ? clamp(el.x + el.size - newSize, SAFE, W - newSize - SAFE)
            : clamp(el.x, SAFE, W - newSize - SAFE);
          const newY = drag.corner.includes('n')
            ? clamp(el.y + el.size - newSize, SAFE, H - newSize - SAFE)
            : clamp(el.y, SAFE, H - newSize - SAFE);
          next.qr = { x: newX, y: newY, size: newSize };
        }
      } else {
        if (drag.action === 'move') {
          next[drag.elemKey] = {
            ...el,
            x: clamp(el.x + dx, SAFE, W - SAFE),
            y: clamp(el.y + dy, SAFE, H - lhMm(el.fontPt) - SAFE),
          };
        } else {
          // resize-font: arrastrar derecha/arriba = más grande
          const newFontPt = clamp(el.fontPt + (dx - dy) * 0.3, 2, 20);
          next[drag.elemKey] = { ...el, fontPt: Math.round(newFontPt * 10) / 10 };
        }
      }

      layoutRef.current = next;
      return next;
    });
  }, [toMm, W, H]);

  const handlePointerUp = useCallback(() => {
    if (dragRef.current) {
      onLayoutChange?.(layoutRef.current);
      dragRef.current = null;
    }
  }, [onLayoutChange]);

  const price = (item.price || '').trim();
  const name  = (item.productName || '').trim();
  const varia = (item.variationLabel || '').trim();

  return (
    <div className="le-wrap">
      <div
        ref={containerRef}
        className="le-canvas"
        style={{ width: Math.round(px(W)), height: Math.round(px(H)) }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClick={(e) => { if (e.target === containerRef.current) setSelected(null); }}
      >
        {/* QR */}
        {layout.qr && (
          <div
            className={`le-el${selected === 'qr' ? ' le-el--sel' : ''}`}
            style={{
              left: px(layout.qr.x), top: px(layout.qr.y),
              width: px(layout.qr.size), height: px(layout.qr.size),
              cursor: 'grab',
            }}
            onPointerDown={(e) => startDrag(e, 'qr', 'move')}
          >
            <span style={{ pointerEvents: 'none', display: 'block', lineHeight: 0 }}>
              <QRCodeSVG
                value={item.qrValue || 'SH-VAR-0'}
                size={Math.max(4, Math.round(px(layout.qr.size)))}
                level="M"
                includeMargin={false}
              />
            </span>
            {selected === 'qr' && ['nw', 'ne', 'sw', 'se'].map(c => (
              <div
                key={c}
                className={`le-h le-h--${c}`}
                onPointerDown={(e) => startDrag(e, 'qr', 'resize', { corner: c })}
              />
            ))}
          </div>
        )}

        {/* Precio */}
        {layout.price && price && (
          <TextEl k="price" layout={layout} text={price} weight={700} color="#000"
            sel={selected} px={px} ptPx={ptPx} startDrag={startDrag} />
        )}

        {/* Nombre */}
        {layout.name && name && (
          <TextEl k="name" layout={layout} text={name} weight={400} color="#111"
            sel={selected} px={px} ptPx={ptPx} startDrag={startDrag} />
        )}

        {/* Variación */}
        {layout.variation && varia && (
          <TextEl k="variation" layout={layout} text={varia} weight={400} color="#555"
            sel={selected} px={px} ptPx={ptPx} startDrag={startDrag} />
        )}
      </div>
      <p className="le-hint">
        Toca para seleccionar · arrastra para mover · arrastra <span className="le-hint--blue">■</span> para cambiar tamaño
      </p>
    </div>
  );
}

function TextEl({ k, layout, text, weight, color, sel, px, ptPx, startDrag }) {
  const el = layout[k];
  if (!el) return null;
  const isSelected = sel === k;
  return (
    <div
      className={`le-el le-el--txt${isSelected ? ' le-el--sel' : ''}`}
      style={{
        left: px(el.x), top: px(el.y),
        fontSize: ptPx(el.fontPt),
        lineHeight: px(lhMm(el.fontPt)) + 'px',
        fontWeight: weight, color,
        whiteSpace: 'nowrap', cursor: 'grab',
      }}
      onPointerDown={(e) => startDrag(e, k, 'move')}
    >
      {text}
      {isSelected && (
        <div
          className="le-h le-h--se le-h--font"
          title="Arrastra → para agrandar, ← para achicar"
          onPointerDown={(e) => startDrag(e, k, 'resize-font')}
        />
      )}
    </div>
  );
}
